import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import SharedViewAPI from "../../../api/api/SharedScreenAPI";
import { ShareAuthType } from "./ShareScreenDrawer";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";

interface Props {
  viewID: string | undefined;
  pageID: string | undefined;
  recordID: string | undefined;
  documentID?: string;
  authentication: ShareAuthType;
  password: string;
  onError: (err: string) => void;
  onSuccess: () => void;
}

export default function useUpdateSharedScreen() {
  const client = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);

  const updateSharedScreen = useMutation({
    mutationFn: ({
      viewID,
      recordID,
      authentication,
      password,
      pageID,
      documentID,
    }: Props) => {
      if (!workspaceID) return Promise.reject("Missing workspace ID!");

      return SharedViewAPI.updateSharedScreen(
        workspaceID,
        viewID,
        authentication,
        password,
        recordID,
        pageID,
        documentID
      ).then((data) => data.data.data);
    },
    onSuccess: (
      newData,
      { viewID, pageID, recordID, documentID, onSuccess }
    ) => {
      if (viewID) {
        client.setQueryData(["sharedView", viewID, recordID], (prev: any) => {
          return {
            ...prev,
            ...newData,
          };
        });
      }

      if (pageID) {
        client.setQueryData(["sharedPage", pageID], (prev: any) => {
          return {
            ...prev,
            ...newData,
          };
        });
      }

      if (documentID) {
        client.setQueryData(["sharedDocument", documentID], (prev: any) => {
          return {
            ...prev,
            ...newData,
          };
        });
      }

      onSuccess();
    },
    onError: (e, { onError }) => onError(getErrorMessage(e)),
  });

  return {
    updateSharedScreen: updateSharedScreen.mutate,
    updatingScreen: updateSharedScreen.isPending,
  };
}
